import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Plus, Pencil, Trash } from "lucide-react";
import EditUserModal from "../components/users/EditUserModal";
import usePagedList from "../hooks/usePagedList";
import PaginationBar from "../components/ui/PaginationBar";
import { getStoredUser } from "../utils/authUser";

const Users = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("current");
  const [errorMessage, setErrorMessage] = useState("");

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const pg = usePagedList(users);

  const loggedUser = getStoredUser();
  const activeTenantName = localStorage.getItem("active_tenant_name") || "";
  const isPlatformAdmin = !!loggedUser?.is_platform_admin;
  const isAdmin =
    loggedUser?.profile?.toLowerCase() === "administrador" ||
    loggedUser?.profile?.toLowerCase() === "admin";

  useEffect(() => {
    fetchUsers();
  }, [scope]);

  useEffect(() => {
    if (!isPlatformAdmin) return;

    const fetchTenants = async () => {
      try {
        const response = await api.get("/tenants/");
        setTenants(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching tenants:", error);
        setTenants([]);
      }
    };

    fetchTenants();
  }, [isPlatformAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const response = await api.get("/users/", {
        params: isPlatformAdmin ? { scope } : {},
      });
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      setErrorMessage(
        error.response?.data?.detail || "Erro ao carregar usuários."
      );
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setSelectedUser(null);
    setIsEditOpen(false);
  };

  const handleEditSubmit = async (formData) => {
    if (!selectedUser) return;

    try {
      await api.put(`/users/${selectedUser.id}`, formData);
      await fetchUsers();
      closeEdit();
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);
      alert("Erro ao atualizar usuário.");
    }
  };

  const handleDelete = async (user) => {
    if (user.can_delete) {
      const confirmDelete = window.confirm(
        `Tem certeza que deseja excluir o usuário "${user.first_name} ${user.last_name}"?`
      );

      if (!confirmDelete) return;

      try {
        await api.delete(`/users/${user.id}`);
        await fetchUsers();
      } catch (error) {
        console.error("Erro ao excluir:", error);
        alert(error.response?.data?.detail || "Erro ao excluir usuário.");
      }
      return;
    }

    if (!user.can_deactivate) {
      alert("O usuário logado atualmente não pode ser desabilitado.");
      return;
    }

    const confirmDeactivate = window.confirm(
      `O usuário "${user.first_name} ${user.last_name}" possui vínculos no sistema. Deseja desabilitá-lo?`
    );

    if (!confirmDeactivate) return;

    try {
      await api.put(`/users/${user.id}`, { is_active: false });
      await fetchUsers();
    } catch (error) {
      console.error("Erro ao desabilitar:", error);
      alert(error.response?.data?.detail || "Erro ao desabilitar usuário.");
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="users-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="users-title">
              Usuários
            </h1>
            <p className="text-gray-500">Gerencie usuários do sistema</p>
            {activeTenantName && scope === "current" && (
              <p className="text-sm text-blue-700 font-medium mt-1">
                Empresa ativa: {activeTenantName}
              </p>
            )}
            {isPlatformAdmin && scope === "all" && (
              <p className="text-sm text-blue-700 font-medium mt-1">Exibindo usuários de todas as empresas</p>
            )}
            {isPlatformAdmin && scope === "unassigned" && (
              <p className="text-sm text-amber-700 font-medium mt-1">Exibindo usuários sem empresa vinculada</p>
            )}
          </div>

          {isAdmin && (
            <Button onClick={() => navigate("/users/new")} data-testid="new-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>
          )}
        </div>

        {isPlatformAdmin && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setScope("current")}
              className={`rounded-md border px-3 py-2 text-sm font-medium ${
                scope === "current" ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 text-gray-700"
              }`}
            >
              Empresa ativa
            </button>
            <button
              onClick={() => setScope("unassigned")}
              className={`rounded-md border px-3 py-2 text-sm font-medium ${
                scope === "unassigned" ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 text-gray-700"
              }`}
            >
              Sem empresa
            </button>
            <button
              onClick={() => setScope("all")}
              className={`rounded-md border px-3 py-2 text-sm font-medium ${
                scope === "all" ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 text-gray-700"
              }`}
            >
              Todos
            </button>
          </div>
        )}

        <Card>
          <CardContent className="pt-6">
            {errorMessage && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {errorMessage}
              </div>
            )}
            {loading ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="no-users">
                Nenhum usuário encontrado
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px]" data-testid="users-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Nome</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Email</th>
                      {isPlatformAdmin && (
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Empresa</th>
                      )}
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Perfil</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Função / Fila</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                      {isAdmin && (
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">
                          Ações
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {pg.pageItems.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        data-testid={`user-row-${user.id}`}
                      >
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{user.email}</td>
                        {isPlatformAdmin && (
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {user.tenant_name || "Sem empresa"}
                          </td>
                        )}
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                            {user.profile}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-700">{user.job_role || "—"}</span>
                          {user.is_assignable && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">
                              Fila
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              user.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {user.is_active ? "Ativo" : "Inativo"}
                          </span>
                        </td>

                        {isAdmin && (
                          <td className="py-3 px-4 text-right text-sm space-x-2">
                            <Button
                              variant="outline"
                              onClick={() => openEdit(user)}
                              className="h-auto px-3 py-1 text-xs text-gray-700"
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Editar
                            </Button>

                            <Button
                              variant="outline"
                              onClick={() => handleDelete(user)}
                              disabled={!user.can_delete && !user.can_deactivate}
                              className={`h-auto px-3 py-1 text-xs ${
                                !user.can_delete && !user.can_deactivate
                                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                                  : user.can_delete
                                  ? "border-red-300 text-red-700 hover:bg-red-50"
                                  : "border-amber-300 text-amber-700 hover:bg-amber-50"
                              }`}
                            >
                              <Trash className="w-4 h-4 mr-1" />
                              {!user.can_delete && !user.can_deactivate
                                ? "Atual"
                                : user.can_delete
                                ? "Excluir"
                                : "Desabilitar"}
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <PaginationBar
                  page={pg.page}
                  pageSize={pg.pageSize}
                  total={pg.total}
                  onPageChange={pg.onPageChange}
                  onPageSizeChange={pg.onPageSizeChange}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <EditUserModal
        isOpen={isEditOpen}
        onClose={closeEdit}
        user={selectedUser}
        onSubmit={handleEditSubmit}
        tenants={tenants}
        isPlatformAdmin={isPlatformAdmin}
      />
    </Layout>
  );
};

export default Users;
